import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ArrowLeft, BarChart3, Eye, Star, X, CalendarDays, Share2, FileText, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { startupsApi, startupStatusApi, analysisApi } from '@/services/api'
import { useToast } from '@/hooks/use-toast'

const headingFont = { fontFamily: '"Outfit", sans-serif' }

export function StartupAnalysisBanner({ onReanalyzeClick }: { onReanalyzeClick?: () => void }) {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: startup } = useQuery({
    queryKey: ['startup', id],
    queryFn: () => startupsApi.get(id!),
    enabled: !!id,
  })

  const { data: latestAnalysis } = useQuery({
    queryKey: ['latestAnalysis', id],
    queryFn: () => analysisApi.getLatest(id!),
    enabled: !!id,
  })

  const { data: startupStatus } = useQuery({
    queryKey: ['startupStatus', id],
    queryFn: () => startupStatusApi.getStatus(id!),
    enabled: !!id,
  })

  const [currentStatus, setCurrentStatus] = useState<string>('New')
  useEffect(() => {
    if (startupStatus?.status) {
      setCurrentStatus(startupStatus.status)
    }
  }, [startupStatus])

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: string) => startupStatusApi.updateStatus(id!, newStatus),
    onSuccess: (data) => {
      setCurrentStatus(data.status)
      queryClient.invalidateQueries({ queryKey: ['startups'] })
      queryClient.invalidateQueries({ queryKey: ['startupStatus', id] })
    }
  })

  const handleStatusUpdate = (newStatus: string) => {
    updateStatusMutation.mutate(newStatus)
  }

  const handleScheduleMeeting = () => {
    toast({ title: 'Meeting Scheduled', description: 'Invitation sent to founders.', duration: 3000 })
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href)
    toast({ title: 'Link Copied', description: 'Analysis link copied to clipboard.', duration: 3000 })
  }

  const parseInvestmentRecommendation = (recommendation: any) => {
    if (!recommendation) return 'PENDING'
    if (typeof recommendation === 'string') {
      try {
        const parsed = JSON.parse(recommendation)
        return parsed.recommendation || parsed.investment_recommendation || recommendation
      } catch {
        return recommendation
      }
    }
    return recommendation.recommendation || recommendation.investment_recommendation || 'PENDING'
  }

  const investabilityScore = latestAnalysis?.investability_score || {
    investment_recommendation: parseInvestmentRecommendation(latestAnalysis?.investment_recommendation),
  }

  return (
    <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700/50 p-4 md:p-5 relative">
      <div className="absolute inset-0 bg-logo-gradient opacity-100"></div>
      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-4 md:gap-6">
          {/* Left - Back Button + Company Details */}
          <div className="flex flex-col md:flex-row items-center md:items-start gap-4 flex-1 w-full">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full mb-2 md:mb-0 h-8 w-8"
            >
              <Link to={`/startups/${id}`}>
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </Button>

            {startup?.profile?.company_info?.logo_url ? (
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-white p-1.5 shadow-xl border border-white/50 flex-shrink-0 flex items-center justify-center overflow-hidden">
                <img
                  src={startup.profile.company_info.logo_url}
                  alt={`${startup.profile.company_info.name} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-12 h-12 md:w-16 md:h-16 bg-logo-gradient rounded-xl flex items-center justify-center shadow-xl border border-white/20 flex-shrink-0">
                <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
            )}
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs md:text-sm text-white/80 mb-0.5 font-bold">Investment Analysis</p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-display font-black text-white dark:text-slate-900 mb-2 tracking-tight" style={headingFont}>
                {startup?.profile?.company_info?.name || latestAnalysis?.company_name || 'Startup Analysis'}
              </h1>
              <div className="flex items-center justify-center md:justify-start gap-2 text-slate-600 dark:text-slate-400 text-[10px] md:text-xs font-bold flex-wrap">
                <span className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1 rounded-full shadow-sm">
                  <span className="w-1.5 h-1.5 bg-[#4194F2] rounded-full" />
                  {investabilityScore?.investment_recommendation || 'Pending'}
                </span>
                {onReanalyzeClick && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onReanalyzeClick}
                    className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1 rounded-full shadow-sm text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-50 transition-all h-auto text-[10px] md:text-xs"
                  >
                    <RefreshCw className="w-3 h-3 text-[#A378CF]" />
                    Re-analyze
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Right - Status & Action Buttons */}
          <div className="flex items-center justify-center lg:justify-end gap-3 flex-wrap w-full lg:w-auto">
            <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-2xl px-5 py-3 border border-slate-200 dark:border-slate-700 shadow-lg">
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Status</span>
              <Badge className="text-sm font-black px-4 py-1.5 bg-logo-gradient text-white border-none shadow-md">
                {currentStatus}
              </Badge>
            </div>

            <div className="w-full h-px lg:w-px lg:h-12 bg-white/20 my-2 lg:my-0 lg:mx-2" />

            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusUpdate('On Watch')}
                disabled={updateStatusMutation.isPending}
                className={`h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20 ${currentStatus === 'On Watch' ? 'bg-white/30 border-white' : ''}`}
              >
                <Eye className="w-4 h-4 mr-2" />
                Watch
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusUpdate('Shortlist')}
                disabled={updateStatusMutation.isPending}
                className={`h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20 ${currentStatus === 'Shortlist' ? 'bg-white/30 border-white' : ''}`}
              >
                <Star className="w-4 h-4 mr-2" />
                Shortlist
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusUpdate('Pass')}
                disabled={updateStatusMutation.isPending}
                className={`h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20 ${currentStatus === 'Pass' ? 'bg-white/30 border-white' : ''}`}
              >
                <X className="w-4 h-4 mr-2" />
                Pass
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleScheduleMeeting}
                className="h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Meeting
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>

              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-10 px-4 rounded-xl border-white/20 bg-white/10 text-white font-bold transition-all hover:bg-white/20"
              >
                <Link to={`/startups/${id}/cheat-sheet`}>
                  <FileText className="w-4 h-4 mr-2" />
                  Cheat Sheet
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
